import { type FC, useState } from 'react';

import {
  Button,
  ButtonGroup,
  Divider,
  HStack,
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  IconExcel,
  IconPlus,
  IconRightArrow,
  IconUserPlus,
} from '@/components/Icons';
import { PopoverItemRow } from '@/components/Popover';

import { AddOrEditSurveyParticipantModal } from './AddOrEditSurveyParticipantModal';
import { ImportParticipantsExcelModal } from './ImportParticipantsExcelModal';
import { usePageAdminSurveyContext } from './PageAdminSurvey.context';

export const AddSurveyParticipant: FC<{ surveyId: string }> = ({
  surveyId,
}) => {
  const { t } = useTranslation(['common', 'survey', 'candidate']);
  const { isEditable } = usePageAdminSurveyContext();
  const [isSingleParticipantAddModalOpen, setIsSingleParticipantAddModalOpen] =
    useState(false);
  const [isAddWithExcelModalOpen, setIsAddWithExcelModalOpen] = useState(false);

  const popover = useDisclosure();

  const addParticipantOptions = [
    {
      id: 'addNewParticipant',
      text: t('survey:addNewParticipant'),
      icon: <IconUserPlus fontSize="20px" mr={2} />,
      onClick: () => setIsSingleParticipantAddModalOpen(true),
      isDisabled: !isEditable,
    },
    {
      id: 'addFromExcel',
      text: t('survey:addParticipantsWithExcel'),
      icon: <IconExcel fontSize="20px" mr={2} />,
      onClick: () => setIsAddWithExcelModalOpen(true),
      isDisabled: !isEditable,
    },
  ] as const;

  return (
    <HStack>
      <Popover
        closeOnEsc
        closeOnBlur
        placement="bottom-start"
        returnFocusOnClose={false}
        isOpen={popover.isOpen}
        onClose={popover.onClose}
      >
        <PopoverAnchor>
          <ButtonGroup
            variant="@primary"
            size="md"
            isAttached
            onClick={popover.onToggle}
            isDisabled={!isEditable}
          >
            <Button
              _hover={{
                bg: 'brand.700',
              }}
              leftIcon={<IconPlus />}
            >
              {t('survey:addNewParticipant', {
                defaultValue: 'Add participant',
              })}
            </Button>
            <IconButton
              aria-label="add participant"
              icon={
                <IconRightArrow
                  transform={`${popover.isOpen ? 'rotate(270deg)' : 'rotate(90deg)'}`}
                  transition="0.3s"
                />
              }
              _hover={{
                bg: 'brand.700',
              }}
            />
          </ButtonGroup>
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            w="fit-content"
            borderRadius="xl"
            borderColor="gray.100"
            shadow="xl"
          >
            <PopoverBody p={0}>
              {addParticipantOptions.map((item, index) => {
                return (
                  <>
                    <PopoverItemRow
                      showArrow={false}
                      key={item.id}
                      name={item.text}
                      icon={item.icon}
                      onRowClick={item.onClick}
                      isDisabled={item.isDisabled}
                    />
                    {index !== addParticipantOptions.length - 1 && <Divider />}
                  </>
                );
              })}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>

      <ImportParticipantsExcelModal
        surveyId={surveyId}
        isOpen={isAddWithExcelModalOpen}
        onClose={() => setIsAddWithExcelModalOpen(false)}
      />

      <AddOrEditSurveyParticipantModal
        isOpen={isSingleParticipantAddModalOpen}
        onClose={() => setIsSingleParticipantAddModalOpen(false)}
        surveyId={surveyId}
      />
    </HStack>
  );
};
